import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { VentaRecord } from '../../domain/entities/VentaRecord';

type Moneda = 'COP' | 'MXN';

const formatCurrency = (amount: number, moneda: Moneda) => {
  return new Intl.NumberFormat(moneda === 'MXN' ? 'es-MX' : 'es-CO', {
    style: 'currency',
    currency: moneda,
    minimumFractionDigits: moneda === 'COP' ? 0 : 2
  }).format(amount);
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('es-CO', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

const MARGEN_IZQ = 5;
const MARGEN_DER = 75;
const COL_VALOR = 30;
const ANCHO_VALOR_ID = MARGEN_DER - COL_VALOR;
const ALTO_LINEA = 4;

const dibujarIdentificadorPedido = (doc: jsPDF, venta: VentaRecord, y: number): number => {
  const identificadoresPedido = [
    ...(venta.idPedidoSAP ? [String(venta.idPedidoSAP)] : []),
    ...(venta.guias ?? []),
  ];
  const tieneIdentificador = identificadoresPedido.length > 0;
  const valor = tieneIdentificador
    ? identificadoresPedido.join(', ')
    : String(venta.compraId ?? '—');
  doc.setFont('helvetica', 'bold');
  doc.text(tieneIdentificador ? 'Pedido SAP/Guía:' : 'N° de Compra:', MARGEN_IZQ, y);
  doc.setFont('helvetica', 'normal');
  const lineas = doc.splitTextToSize(valor, ANCHO_VALOR_ID);
  doc.text(lineas, COL_VALOR, y);
  return y + Math.max(0, lineas.length - 1) * ALTO_LINEA;
};

const dibujarTotales = (doc: jsPDF, venta: VentaRecord, moneda: Moneda, startY: number): number => {
  const money = (amount: number) => formatCurrency(amount, moneda);
  doc.setFontSize(8);
  if (venta.ivaEnvios === undefined) {
    doc.text('Subtotal:', 35, startY);
    doc.text(money(venta.subtotal ?? 0), 75, startY, { align: 'right' });
    startY += 4;
    doc.text('Impuestos:', 35, startY);
    doc.text(money(venta.impuestos ?? 0), 75, startY, { align: 'right' });
    return startY;
  }

  doc.text('Base gravable:', 30, startY);
  doc.text(money(venta.baseGravable ?? 0), 75, startY, { align: 'right' });
  if ((venta.ivaEnvios ?? 0) > 0) {
    startY += 4;
    doc.text('IVA Envíos 16%:', 30, startY);
    doc.text(money(venta.ivaEnvios), 75, startY, { align: 'right' });
  }
  if ((venta.ivaEmpaques ?? 0) > 0) {
    startY += 4;
    doc.text('IVA Empaques 16%:', 30, startY);
    doc.text(money(venta.ivaEmpaques ?? 0), 75, startY, { align: 'right' });
  }
  startY += 4;
  doc.text('Total Impuestos:', 30, startY);
  doc.text(money(venta.totalImpuestos ?? 0), 75, startY, { align: 'right' });
  return startY;
};

const resolverClienteTicket = (venta: VentaRecord, moneda: Moneda) => {
  const esGenericoCO = moneda !== 'MXN' && (
    !venta.cliente.nombre || venta.cliente.nombre === 'Público en General'
  );
  if (esGenericoCO) {
    return { nombreCliente: 'Consumidor Final', tipoDocumentoCliente: 'NIT', documentoCliente: '222222222222' };
  }
  return {
    nombreCliente: venta.cliente.nombre || 'Público en General',
    tipoDocumentoCliente: venta.cliente.tipoDocumento || 'RFC',
    documentoCliente: venta.cliente.documento || 'XAXX010101000',
  };
};

const opcionesTablaItems = (tieneGuia: boolean, items: VentaRecord['items'], moneda: Moneda) => {
  const head = tieneGuia
    ? [[
        { content: 'Cant', styles: { halign: 'center' as const } },
        'Descripción',
        'Guía',
        { content: 'Subtotal', styles: { halign: 'right' as const } },
      ]]
    : [[
        { content: 'Cant', styles: { halign: 'center' as const } },
        'Descripción',
        { content: 'Subtotal', styles: { halign: 'right' as const } },
      ]];
  const body = items.map((item) => {
    const guia = item.guia ?? '';
    return tieneGuia
      ? [item.cantidad.toString(), item.descripcion, guia, formatCurrency(item.subtotal, moneda)]
      : [item.cantidad.toString(), item.descripcion, formatCurrency(item.subtotal, moneda)];
  });
  const columnStyles = (tieneGuia
    ? { 0: { cellWidth: 9, halign: 'center' as const }, 1: { cellWidth: 22 }, 2: { cellWidth: 20 }, 3: { cellWidth: 19, halign: 'right' as const } }
    : { 0: { cellWidth: 10, halign: 'center' as const }, 1: { cellWidth: 35 }, 2: { cellWidth: 25, halign: 'right' as const } }
  ) as Record<string, { cellWidth: number; halign?: 'center' | 'right' }>;
  return { head, body, columnStyles };
};

export const generarTiquetePDF = (venta: VentaRecord) => {
  // Crear un documento PDF con tamaño de ticket (ej. 80mm de ancho)
  // 80mm = ~3.15 pulgadas. Usaremos un ancho de 80mm y alto dinámico
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: [80, 200] // 80mm de ancho, 200mm de alto (se puede ajustar)
  });

  const moneda: Moneda = venta.moneda ?? 'COP';

  // Configuración de fuente
  doc.setFont('helvetica');
  
  // Cabecera
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(moneda === 'MXN' ? 'COORDI' : 'Coordinadora', 40, 10, { align: 'center' });
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('TICKET DE VENTA', 40, 15, { align: 'center' });
  
  doc.setFontSize(8);
  doc.text(`Terminal: ${venta.terminal}`, 40, 20, { align: 'center' });
  doc.text(`Equipo: ${venta.equipo}`, 40, 24, { align: 'center' });
  
  doc.line(MARGEN_IZQ, 28, MARGEN_DER, 28);

  // Datos de la factura. Regla de negocio (2026-08-24): el mostrador no
  // puede esperar los ~2+ minutos que tarda SIFA en generar el numeroPedido
  // de una guía — ya no se usa como identificador aquí. En su lugar se
  // consolida, sin esperar nada, el Sales Order de empaque (idPedidoSAP,
  // síncrono) y la(s) guía(s) de envío (codigosRemision, también síncronas)
  // en un solo identificador "Pedido SAP/Guía" separado por comas (ej.
  // "3916, 20332003155"). Si no hay ninguno de los dos (compra sin
  // identificadores, caso residual), se usa el compraId. jsPDF lanza una
  // excepción si se le pasa `null` directamente a `.text()`.
  // Con 3+ guías el valor no cabe en 80mm: se parte en líneas y se empuja
  // Fecha/Cliente para que no se corte el último número.
  let y = dibujarIdentificadorPedido(doc, venta, 33);

  y += ALTO_LINEA;
  doc.setFont('helvetica', 'bold');
  doc.text('Fecha:', MARGEN_IZQ, y);
  doc.setFont('helvetica', 'normal');
  doc.text(formatDate(venta.fecha), COL_VALOR, y);

  const { nombreCliente, tipoDocumentoCliente, documentoCliente } = resolverClienteTicket(venta, moneda);

  y += 6;
  doc.setFont('helvetica', 'bold');
  doc.text('Cliente:', MARGEN_IZQ, y);
  doc.setFont('helvetica', 'normal');
  y += ALTO_LINEA;
  doc.text(nombreCliente, MARGEN_IZQ, y);
  y += ALTO_LINEA;
  doc.text(`${tipoDocumentoCliente}: ${documentoCliente}`, MARGEN_IZQ, y);

  y += ALTO_LINEA;
  doc.line(MARGEN_IZQ, y, MARGEN_DER, y);

  let startY = y + 5;

  // Mostrar columna "Guía" si al menos un ítem tiene guía asignada
  const tieneGuia = venta.items?.some(item => item.guia) ?? false;

  if (venta.items && venta.items.length > 0) {
    const { head, body, columnStyles } = opcionesTablaItems(tieneGuia, venta.items, moneda);
    autoTable(doc, {
      startY: startY,
      margin: { left: 5, right: 5 },
      // "Subtotal" (no "Total"): esta columna es el precio del ítem SIN
      // impuestos. El IVA se desglosa aparte más abajo (Base gravable/IVA
      // Envíos/IVA Empaques/Total Impuestos) y el TOTAL final de la
      // sección de totales sí los incluye — evita que se confunda con el
      // total por pedido (con impuestos) que muestra el historial.
      head,
      body,
      theme: 'plain',
      styles: { fontSize: 7, cellPadding: 1 },
      headStyles: { fontStyle: 'bold' },
      columnStyles,
    });
    
    startY = (doc as any).lastAutoTable.finalY + 5;
  } else {
    // Fallback por si hay ventas antiguas sin items guardados
    doc.text('Items no detallados en historial', 5, startY);
    startY += 10;
  }

  doc.line(5, startY, 75, startY);
  startY += 5;

  startY = dibujarTotales(doc, venta, moneda, startY);

  startY += 6;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('TOTAL:', 35, startY);
  doc.text(formatCurrency(venta.total, moneda), 75, startY, { align: 'right' });

  startY += 8;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text('Método de pago:', 5, startY);
  doc.text(venta.metodoPago, 75, startY, { align: 'right' });

  if (moneda === 'MXN') {
    startY += 15;
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    const leyenda = doc.splitTextToSize(
      'Para facturar ingrese a www.coordi.com y coloque los datos conforme a su constancia de situación fiscal. Recuerda solicitar la factura en el mismo mes de la compra.',
      75
    );
    doc.text(leyenda, 40, startY, { align: 'center' });
    startY += leyenda.length * 4 + 4;
  } else {
    startY += 10;
  }

  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  doc.text('¡Gracias por su compra!', 40, startY, { align: 'center' });

  // Abrir el PDF en una nueva pestaña
  window.open(doc.output('bloburl'), '_blank');
};